import React, { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DataContext } from "../dataContext";
import phone from "phone";
import useApi from "../api/api";
import { ReactComponent as Clock } from "../assets/ico_clock.svg";
import { ROUTE_CONSTANTS } from "../constants/routeConstants";
import { getOS, isMobile } from "../utils/utils";
import Select from "react-select";
import { ReactComponent as Cancel } from "../assets/ico_cancel.svg";
import { ReactComponent as Alert } from "../assets/ico_alert.svg";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import RecipientTypeSelector from "../components/RecipientTypeSelector";
import { SpinnerCircularFixed } from "spinners-react";

const SendMoney = ({
  dropDownStyle,
  dropDownTheme,
  setFromDemoPage,
  isScheduled,
}) => {
  const { TRANSFER_PROCESSING, SCHEDULE_PAYMENT, SCHEDULED_DETAILS } =
    ROUTE_CONSTANTS;
  const {
    accounts,
    cards,
    loggedIn,
    scheduledAlert,
    setScheduledAlert,
    getScheduledTransfers,
    p2pConfig,
  } = useContext(DataContext);
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  const api = useApi(false);
  const schedulerApi = useApi(true);
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState(undefined);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [fromAccount, setFromAccount] = useState(undefined);
  const [disableSendToPhone, setDisableSendToPhone] = useState(true);
  const [isValidInput, setIsValidInput] = useState(false);
  const [amount, setAmount] = useState(params.amount || "");
  const [recipient, setRecipient] = useState(params.recipient || "");
  const [chosenCard, setChosenCard] = useState(cards[0]);
  const [chosenAccount, setChosenAccount] = useState(undefined);
  const [note, setNote] = useState("");
  const [repeatFrequency, setRepeatFrequency] = useState({
    label: "Never",
    value: "NEVER",
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // Set initial recipient type based on interface mode
  const getInitialRecipientType = () => {
    if (p2pConfig?.p2p_interface_mode === 'FedNowDirectOnly') {
      return "bank_account";
    }
    return "phone_email";
  };
  const [recipientType, setRecipientType] = useState(getInitialRecipientType());
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountNumberConfirm, setAccountNumberConfirm] = useState("");

  const repeatFrequencyOptions = useMemo(
    () => [
      {
        label: "Never",
        value: "NEVER",
      },
      {
        label: "Weekly",
        value: "WEEKLY",
      },
      {
        label: "Monthly",
        value: "MONTHLY",
      },
    ],
    []
  );

  useEffect(() => {
    setFromDemoPage(false);
  });


  useEffect(() => {
    if (getOS() === "macos" || getOS() === "ios" || isMobile()) {
      setDisableSendToPhone(false);
    }
  }, []);

  // Update recipientType when p2pConfig loads or changes
  useEffect(() => {
    if (p2pConfig?.p2p_interface_mode === 'FedNowDirectOnly') {
      setRecipientType("bank_account");
    } else if (p2pConfig?.p2p_interface_mode === 'P2POnly') {
      setRecipientType("phone_email");
    }
    // For 'Both' mode, keep user's selection
  }, [p2pConfig?.p2p_interface_mode]);

  useEffect(() => {
    if (loggedIn && accounts.length) {
      if (recipientType === "bank_account") {
        // For FedNow, use the chosenAccount directly
        if (chosenAccount) {
          const account = accounts.find(acc => acc.id === chosenAccount.value);
          console.log("Setting fromAccount for bank_account:", account);
          setFromAccount(account);
        } else {
          // If no chosenAccount yet, default to first account
          const ckAccount = accounts.find(acc => acc.code === "CK");
          const defaultAccount = ckAccount || accounts[0];
          console.log("No chosenAccount, defaulting to:", defaultAccount);
          setFromAccount(defaultAccount);
      }
      } else {
        // For phone/email
        // If rail_type='account', use chosenAccount (no cards)
        // Otherwise, use card-based account selection
        if (p2pConfig.rail_type === 'account') {
          if (chosenAccount) {
            const account = accounts.find(acc => acc.id === chosenAccount.value);
            console.log("Setting fromAccount for rail_type=account with phone/email:", account);
            setFromAccount(account);
          } else {
            // If no chosenAccount yet, default to first account
            const ckAccount = accounts.find(acc => acc.code === "CK");
            const defaultAccount = ckAccount || accounts[0];
            console.log("No chosenAccount, defaulting to:", defaultAccount);
            setFromAccount(defaultAccount);
          }
        } else {
          // For phone/email with cards, the from account depends on the card chosen
          // we want to set the account to the CK account if it exists, otherwise use the other/SV account
          let fromAccount = accounts
            .filter((account) =>
              account.card_uuids.includes(chosenCard?.value)
            )
            .sort((a, b) => a.code.localeCompare(b.code)) // sort by code, alphabetically (so CK is before SV)
            [0];
          setFromAccount(fromAccount);
        }
      }
    }
  }, [loggedIn, accounts, chosenCard, chosenAccount, recipientType, p2pConfig.rail_type]);


  // on cards load set the chosen card
  useEffect(() => {
    if (cards.length) {
      setChosenCard({
        label: cards[0].display_label,
        value: cards[0].uuid,
      });
    }
  }, [cards]);

  // on accounts load set the chosen account (for FedNow)
  useEffect(() => {
    if (accounts.length && !chosenAccount) {
      // Default to checking account if available, otherwise first account
      const ckAccount = accounts.find(acc => acc.code === "CK");
      const defaultAccount = ckAccount || accounts[0];
      // Mask account number - show last 4 digits from dda field
      const accountNumber = defaultAccount.dda || "";
      const cleanAccountNumber = accountNumber.includes("_")
        ? accountNumber.split("_").pop()
        : accountNumber;
      const masked = cleanAccountNumber.length > 4
        ? `****${cleanAccountNumber.slice(-4)}`
        : cleanAccountNumber;
      const accountLabel = `${defaultAccount.code === "CK" ? "Checking" : "Savings"} ${masked}`;
      setChosenAccount({
        label: accountLabel,
        value: defaultAccount.id,
      });
    }
  }, [accounts, chosenAccount]);

  const validateRecipient = () => {
    const recipientType = recipient.includes("@") ? "email" : "phone";

    let recipientValue = recipient.trim().toLowerCase();
    if (recipientType === "phone") {
      if (disableSendToPhone) {
        return [];
      }

      const parsed = phone(recipientValue, { country: "USA" });
      if (!parsed.isValid) {
        return [];
      }
      recipientValue = parsed.phoneNumber;
    } else if (recipientType === "email") {
      const e =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (!e.test(recipientValue)) {
        return [];
      }
    }
    return [{ type: recipientType, value: recipientValue }];
  };

  const validateAmount = () => {
    let newError = false;

    const amtVal = parseFloat(amount);
    if (amtVal < 0.0) {
      newError = true;
    }

    // return false = valid, no errors
    return !newError;
  };

  const handleRecipientChange = ({ target: { value } }) => {
    setRecipient(value ? value : "");
    if (
      (value.match(/^[0-9, +, \-, (, )]+$/) != null && value.length > 9) ||
      value
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    ) {
      setIsValidInput(true);
    } else {
      setIsValidInput(false);
    }
  };

  useEffect(() => {
    if (
      (params.recipient?.match(/^[0-9, +, \-, (, )]+$/) != null &&
        params.recipient?.length > 9) ||
      params.recipient
        ?.toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    ) {
      setIsValidInput(true);
    }
    // we do not need params in this dependency array, ignore warning
    // eslint-disable-next-line
  }, []);

  const flashError = (e) => {
    setErrorMessage(e);
    setTimeout(() => {
      setErrorMessage(undefined);
    }, 5000);
  };

  const closeScheduledAlert = () => {
    setScheduledAlert(false);
  };

  const fixScheduledAlert = (e) => {
    e.preventDefault();
    console.log("fix alert");
    // TODO handle scheduled alert issue
  };

  const clickClock = (e) => {
    e.preventDefault();
    navigate(SCHEDULE_PAYMENT);
  };

  const formatDate = (date) => {
    const formatStartDate = new Date(date);
    const year = formatStartDate.getFullYear();
    const month = String(formatStartDate.getMonth() + 1).padStart(2, "0");
    const day = String(formatStartDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const isValidRouting = (rn) => {
    if (!/^\d{9}$/.test(rn)) return false;
    const digits = rn.split("").map((d) => parseInt(d, 10));
    const sum =
      3 * (digits[0] + digits[3] + digits[6]) +
      7 * (digits[1] + digits[4] + digits[7]) +
      1 * (digits[2] + digits[5] + digits[8]);
    return sum % 10 === 0;
  };

  const isValidAccountNumber = (an) => /^\d{4,17}$/.test(an);

  const isValidEmail = (email) => {
    if (!email) return true; // Email is optional
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidBankTransfer = () => {
    return (
      recipientName.trim() &&
      isValidRouting(routingNumber) &&
      isValidAccountNumber(accountNumber) &&
      accountNumber === accountNumberConfirm &&
      isValidEmail(recipientEmail) &&
      validateAmount()
    );
  };

  const handleSubmit = async (e) => {
    setIsSubmitted(true);
    e.preventDefault();

    try {
      if (recipientType === "bank_account") {
        // Bank account transfer validation
        if (!fromAccount) {
          flashError("Please select an account");
          return;
        }
        if (!recipientName.trim()) {
          flashError("Please enter recipient name");
          return;
        }
        if (!validateAmount()) {
          flashError("Invalid amount");
          return;
        }
        if (!isValidRouting(routingNumber)) {
          flashError("Invalid routing number");
          return;
        }
        if (!isValidAccountNumber(accountNumber)) {
          flashError("Invalid account number");
          return;
        }
        if (accountNumber !== accountNumberConfirm) {
          flashError("Account numbers do not match");
          return;
        }
        if (!isValidEmail(recipientEmail)) {
          flashError("Invalid email address");
          return;
        }

        const transferData = {
          account: fromAccount.id,
          amount: { type: "USD", value: amount },
          note: note.trim(),
          routing_number: routingNumber,
          account_number: accountNumber,
          recipient_name: recipientName.trim(),
          recipient_email: recipientEmail.trim() || undefined,
          recipient: [], // Backend expects this even for FedNow transfers
        };
        console.log("FedNow Transfer Data:", transferData);
        const response = await api.post("transfer/outgoing", transferData);
        navigate(`${TRANSFER_PROCESSING.replace(":id", response.data.id)}`);
      } else {
        // Phone/email transfer
        const wrappedRecipient = validateRecipient();
        if (wrappedRecipient.length < 1) {
          let errMsg = "Invalid email";
          if (!disableSendToPhone) {
            errMsg = "Invalid email or phone number";
          }
          flashError(errMsg);
          return;
        }
        if (!validateAmount()) {
          flashError("Invalid amount");
          return;
        }

        if (!isScheduled) {
          const transferData = {
            account: fromAccount.id,
            amount: { type: "USD", value: amount },
            card_uuid: chosenCard.value,
            note: note.trim(),
            recipient: wrappedRecipient,
          };
          const response = await api.post("transfer/outgoing", transferData);
          navigate(`${TRANSFER_PROCESSING.replace(":id", response.data.id)}`);
        } else {
          const oneMinuteFromNowLocalDateTime = new Date(
            new Date().getTime() + 1 * 60 * 1000
          );

          const oneMinuteFromNowUTCTimeOnly =
            "T" +
            (
              oneMinuteFromNowLocalDateTime.toISOString().slice(0, -1) + "Z"
            ).split("T")[1];

          let scheduledTransferData = {
            from_card: chosenCard.value,
            from_account: fromAccount.id,
            start_date: formatDate(startDate),
            end_date: formatDate(endDate),
            repeat_frequency: repeatFrequency.value,
            amount: { type: "USD", value: amount },
            recipient: wrappedRecipient[0],
            comment: note.trim(),
          };

          if (repeatFrequency.value === "NEVER") {
            scheduledTransferData = {
              ...scheduledTransferData,
              next_occurrence: startDate + oneMinuteFromNowUTCTimeOnly,
              end_date: null,
            };
          } else if (repeatFrequency.value === "WEEKLY") {
            scheduledTransferData = {
              ...scheduledTransferData,
              next_occurrence:
                new Date(
                  oneMinuteFromNowLocalDateTime.getTime() +
                    7 * 24 * 60 * 60 * 1000
                )
                  .toISOString()
                  .slice(0, -1) + "Z",
            };
          } else if (repeatFrequency.value === "MONTHLY") {
            scheduledTransferData = {
              ...scheduledTransferData,
              next_occurrence:
                new Date(
                  oneMinuteFromNowLocalDateTime.getFullYear(),
                  oneMinuteFromNowLocalDateTime.getMonth() + 1,
                  oneMinuteFromNowLocalDateTime.getDate()
                )
                  .toISOString()
                  .slice(0, -1) + "Z",
            };
          }
          if (recipient.includes("@")) {
            const response = await schedulerApi.post(
              "schedules",
              scheduledTransferData
            );
            getScheduledTransfers();
            navigate(`${SCHEDULED_DETAILS.replace(":id", response.data.id)}`);
          } else {
            flashError(
              "Scheduled transfers are currently only available to email recipients."
            );
          }
        }
      }
    } catch (error) {
      console.error("Send money error", error);
      if (error.response?.status === 500 || error.response?.status === 400) {
        flashError(error.response?.data?.detail);
      } else {
        console.error("An unknown error occurred");
        flashError("An error occurred while sending these funds.");
      }
    } finally {
      setIsSubmitted(false);
    }
  };

  const today = new Date();
  const dateToday = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const twoDaysFromNow = new Date();
  twoDaysFromNow.setDate(twoDaysFromNow.getDate() + 3);
  const dateTwoDaysFromNow = twoDaysFromNow?.toISOString()?.split("T")[0];

  const oneYearFromToday = new Date();
  oneYearFromToday.setDate(oneYearFromToday.getDate() + 366);
  const dateOneYearFromToday = oneYearFromToday?.toISOString()?.split("T")[0];

  const tenYearsFromToday = new Date();
  tenYearsFromToday.setFullYear(tenYearsFromToday.getFullYear() + 10);
  const dateTenYearsFromToday = formatDate(tenYearsFromToday);

  return (
    <form
      onSubmit={(e) => handleSubmit(e)}
      noValidate={true}
      autoComplete="off"
    >
      {accounts.length && (p2pConfig.rail_type === 'account' || cards.length) ? (
        <>
          {scheduledAlert && (
            <div className="alert_message">
              <div style={{ display: "flex" }}>
                <Alert
                  className="fill_sending_bg_primary"
                  style={{ marginRight: "10px" }}
                />
                <div style={{ maxWidth: "70%", textWrap: "balance" }}>
                  There was an issue with a scheduled payment
                  <button
                    className="alert_message"
                    onClick={(e) => fixScheduledAlert(e)}
                  >
                    Fix the Payment
                  </button>
                </div>
              </div>
              <div style={{ width: "auto", height: "20px" }}>
                <Cancel
                  className="fill_sending_text_primary"
                  onClick={() => closeScheduledAlert()}
                />
              </div>
            </div>
          )}
          <RecipientTypeSelector
            selectedType={recipientType}
            onTypeChange={setRecipientType}
            showBankOption={(p2pConfig.p2p_interface_mode === 'FedNowDirectOnly' || p2pConfig.p2p_interface_mode === 'Both') && !isScheduled}
            disableSendToPhone={disableSendToPhone}
            interfaceMode={p2pConfig.p2p_interface_mode}
          />
          {recipientType === "phone_email" ? (
            <>
              <label htmlFor="recipient">Recipient</label>
              <input
                id="recipient"
                value={recipient}
                placeholder={
                  disableSendToPhone || isScheduled
                    ? "Type email"
                    : "Type phone number or email"
                }
                onChange={handleRecipientChange}
              />
            </>
          ) : (
            <>
              <label htmlFor="recipient_name">Recipient Name</label>
              <input
                id="recipient_name"
                value={recipientName}
                placeholder="Enter recipient's name"
                onChange={(e) => setRecipientName(e.target.value)}
              />
              <label htmlFor="recipient_email">Recipient Email</label>
              <input
                id="recipient_email"
                type="email"
                value={recipientEmail}
                placeholder="recipient@example.com"
                onChange={(e) => setRecipientEmail(e.target.value)}
              />
              <label htmlFor="routing_number">Routing Number</label>
              <input
                id="routing_number"
                value={routingNumber}
                placeholder="9-digit routing number"
                inputMode="numeric"
                onChange={(e) => setRoutingNumber(e.target.value.replace(/[^\d]/g, "").slice(0, 9))}
              />
              <label htmlFor="account_number">Account Number</label>
              <input
                id="account_number"
                value={accountNumber}
                placeholder="Account number"
                inputMode="numeric"
                onChange={(e) => setAccountNumber(e.target.value.replace(/[^\d]/g, "").slice(0, 17))}
              />
              <label htmlFor="account_number_confirm">Confirm Account Number</label>
              <input
                id="account_number_confirm"
                value={accountNumberConfirm}
                placeholder="Re-enter account number"
                inputMode="numeric"
                onChange={(e) => setAccountNumberConfirm(e.target.value.replace(/[^\d]/g, "").slice(0, 17))}
              />
            </>
          )}
          <label htmlFor="amount">Amount</label>
          <div
            className="bottom_border_sending_bg_tertiary"
            style={{
              display: "flex",
              marginTop: "-10px",
            }}
          >
            <div
              style={{
                fontSize: "32px",
                fontWeight: 550,
                marginTop: "5px",
              }}
            >
              $
            </div>
            <input
              id="amount"
              value={amount}
              type="number"
              min={1}
              className="bg_sending_bg_primary"
              style={{
                fontSize: "32px",
                fontWeight: 550,
                padding: "0 2px",
              }}
              onChange={(event) => setAmount(event.target.value)}
              onClick={() => validateRecipient()}
              placeholder="0"
            />
          </div>
          {/* For rail_type='account' with phone/email recipient, show accounts dropdown instead of cards */}
          {recipientType === "phone_email" && p2pConfig.rail_type !== 'account' ? (
            <>
              <label htmlFor="select_card">From Card</label>
              <Select
                id="select_card"
                options={cards.map((card) => ({
                  label: card.display_label,
                  value: card.uuid,
                }))}
                value={chosenCard}
                onChange={(option) => setChosenCard(option)}
                classNamePrefix="dropdown"
                // menuIsOpen={true} // use for styling dev
                styles={dropDownStyle}
                theme={dropDownTheme}
              />
            </>
          ) : (
            <>
              <label htmlFor="select_account">From Account</label>
              <Select
                id="select_account"
                options={accounts.map((account) => {
                  // Mask account number - show last 4 digits from dda field
                  const accountNumber = account.dda || "";
                  const cleanAccountNumber = accountNumber.includes("_")
                    ? accountNumber.split("_").pop()
                    : accountNumber;
                  const masked = cleanAccountNumber.length > 4
                    ? `****${cleanAccountNumber.slice(-4)}`
                    : cleanAccountNumber;
                  return {
                    label: `${account.code === "CK" ? "Checking" : "Savings"} ${masked}`,
                    value: account.id,
                  };
                })}
                value={chosenAccount}
                onChange={(option) => setChosenAccount(option)}
                classNamePrefix="dropdown"
                styles={dropDownStyle}
                theme={dropDownTheme}
              />
            </>
          )}
          <label htmlFor="note">Comment (optional)</label>
          <input
            id="note"
            value={note}
            placeholder="Type your comment"
            onChange={(event) => setNote(event.target.value)}
          />
          {isScheduled && (
            <>
              <label htmlFor="start_date">Scheduled date</label>
              <DatePicker
                id="start_date"
                placeholderText="Select scheduled date"
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                minDate={dateTwoDaysFromNow}
                maxDate={dateOneYearFromToday}
              />
              <label htmlFor="repeat_frequency">Repeat</label>
              <Select
                id="repeat_frequency"
                options={repeatFrequencyOptions}
                value={repeatFrequency}
                onChange={(option) => setRepeatFrequency(option)}
                classNamePrefix="dropdown"
                // menuIsOpen={true} // use for styling dev
                styles={dropDownStyle}
                theme={dropDownTheme}
              />
              {repeatFrequency.value !== "NEVER" && (
                <>
                  <label htmlFor="end_date">End date</label>
                  <DatePicker
                    id="end_date"
                    placeholderText="Select end date"
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    minDate={dateTwoDaysFromNow}
                    maxDate={dateTenYearsFromToday}
                  />
                </>
              )}
            </>
          )}
          {errorMessage && <div className="error_message">{errorMessage}</div>}
          {isScheduled ? (
            <div style={{ marginTop: "30px" }}>
              <button
                disabled={
                  !amount ||
                  !recipient ||
                  isSubmitted ||
                  !isValidInput ||
                  !startDate ||
                  startDate < dateToday ||
                  (repeatFrequency.value !== "NEVER" && !endDate) ||
                  (repeatFrequency.value !== "NEVER" && startDate >= endDate)
                }
                type="submit"
                data-cy="save_schedule_button"
              >
                Save
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", marginTop: "30px" }}>
              {p2pConfig.scheduled_recurring_enabled && (
                <button
                  onClick={(e) => clickClock(e)}
                  className="bg_sending_bg_primary border_sending_bg_secondary"
                  style={{
                    width: "66px",
                    marginRight: "20px",
                  }}
                  data-cy="clock_button"
                >
                  <Clock className="fill_sending_bg_secondary" />
                </button>
              )}
              <button
                disabled={
                  recipientType === "phone_email"
                    ? !amount || !recipient || isSubmitted || !isValidInput
                    : !isValidBankTransfer() || isSubmitted
                }
                type="submit"
                data-cy="send_money_button"
              >
                {recipientType === "bank_account" ? "Send to Account" : "Send Payment Now"}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="center_page">
          <div>
            <h3>
            You are not currently enabled to use this service. This could be for a variety of reasons including not having an eligible account or an active card.
            </h3>
            <h4>If you believe you have received this by mistake, please contact customer support.</h4>
          </div>
        </div>
      )}

      {/* Full-page loading overlay */}
      {isSubmitted && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px'
          }}>
            <SpinnerCircularFixed
              size={60}
              thickness={180}
              speed={150}
              color="#007bff"
              secondaryColor="rgba(0, 123, 255, 0.2)"
            />
            <p style={{ margin: 0, fontSize: '16px', color: '#333' }}>
              {recipientType === "bank_account" ? "Verifying routing number capabilities..." : "Processing..."}
            </p>
          </div>
        </div>
      )}
    </form>
  );
};

export default SendMoney;
